import { Box, Checkbox, Chip, List, TextField, Typography } from '@mui/material';
import Grid from '@mui/material/Grid2';
import { ReactNode } from 'react';
import { Virtuoso } from 'react-virtuoso';
import { IManagedUserAccessDataModel } from '../../../data-models/managed-user-access.data-model';
import { AddRemoveListItem } from './AddRemoveListItem';
import { CheckboxListItem } from './CheckboxListItem';
import { LeftPanel, RightPanel } from './ManageAccessPanels';
import { compareByAccessState } from './useManageAccess.view-model';

interface PeopleViewProps {
  companies: Array<{ id: number; name: string }>;
  userList: Array<{ id: number; email: string }>;
  filteredUsers: Array<{ id: number; email: string }>;
  userFilter: string;
  onUserFilterChange: (filter: string) => void;
  selectedUsers: string[];
  onUserCheckboxToggle: (email: string) => void;
  onUserSelection: (email: string) => void;
  onSelectAll: () => void;
  onDeselectAll: () => void;
  onBulkAdd: (identifier: string | number) => void;
  onBulkRemove: (identifier: string | number) => void;
  getAccessForUser: (email: string) => IManagedUserAccessDataModel[];
  getAggregatedAccessStateForCompany: (companyId: number) => 'all' | 'partial' | 'none';
}

export const PeopleView = ({
  companies,
  filteredUsers,
  userFilter,
  onUserFilterChange,
  selectedUsers,
  onUserCheckboxToggle,
  onUserSelection,
  onSelectAll,
  onDeselectAll,
  onBulkAdd,
  onBulkRemove,
  getAccessForUser,
  getAggregatedAccessStateForCompany,
}: PeopleViewProps) => {
  function getTopMessage(count: number) {
    if (count === 0) {
      return 'Select one or more people to manage companies';
    } else if (count === 1) {
      return `Update companies assigned to ${selectedUsers[0]}`;
    } else {
      return (
        <span>
          Update companies assigned to selected <b>{`${count} people`}</b>
        </span>
      );
    }
  }

  function getSecondaryLabel(assignedCount: number): string {
    if (assignedCount === 0) {
      return 'No companies';
    } else if (assignedCount === 1) {
      return '1 company';
    }
    return `${assignedCount} companies`;
  }

  const allSelected = filteredUsers.length > 0 && selectedUsers.length === filteredUsers.length;
  const someSelected = selectedUsers.length > 0 && selectedUsers.length < filteredUsers.length;

  // assumes companies are already sorted alphabetically by name
  const sortedCompanies = companies
    .map((c) => ({ ...c, accessState: getAggregatedAccessStateForCompany(c.id) }))
    .sort(compareByAccessState);

  return (
    <Grid container spacing={4} sx={{ height: '100%' }} flex={1}>
      <LeftPanel
        header={
          <Box>
            <TextField
              fullWidth
              placeholder='Filter people...'
              onChange={(e) => onUserFilterChange(e.target.value)}
              sx={{ m: 0 }}
              value={userFilter}
            />
            <Box sx={{ display: 'flex', alignItems: 'center' }}>
              <Checkbox
                checked={allSelected}
                indeterminate={someSelected}
                onChange={allSelected ? onDeselectAll : onSelectAll}
              />
              <Typography variant='subtitle2'>Select All People</Typography>
            </Box>
          </Box>
        }
        list={
          <List disablePadding={true} sx={{ overflow: 'auto' }}>
            {filteredUsers.map((user) => (
              <CheckboxListItem
                key={user.id}
                checked={selectedUsers.includes(user.email)}
                data={user.email ?? ''}
                id={user.id}
                onCardClick={onUserSelection}
                onCheckboxChange={onUserCheckboxToggle}
                primary={user.email}
                secondary={`${getSecondaryLabel(getAccessForUser(user.email).length)}`}
                selected={selectedUsers.includes(user.email)}
              />
            ))}
          </List>
        }
      ></LeftPanel>

      <RightPanel selectedItemCount={selectedUsers.length} topMessage={getTopMessage(selectedUsers.length)}>
        <Virtuoso
          style={{ width: '100%' }}
          data={sortedCompanies}
          itemContent={(_, company) => {
            function getChip(selectedUsersCount: number, access: 'all' | 'partial' | 'none'): ReactNode {
              if (selectedUsersCount === 1) {
                return access === 'all' ? (
                  <Chip label='Access Granted' color='success' size='small' variant='outlined' />
                ) : (
                  <Chip label='No Access' color='default' size='small' variant='outlined' />
                );
              } else if (access === 'all') {
                return (
                  <Chip
                    label='Access Granted to All Selected'
                    color='success'
                    size='small'
                    variant='outlined'
                  />
                );
              } else if (access === 'partial') {
                return (
                  <Chip
                    label='Access Granted to Some Selected'
                    color='warning'
                    size='small'
                    variant='outlined'
                  />
                );
              } else {
                return <Chip label='No Users Have Access' color='default' size='small' variant='outlined' />;
              }
            }

            return (
              <AddRemoveListItem
                key={company.id}
                primary={company.name}
                secondary={getChip(selectedUsers.length, company.accessState)}
                accessState={company.accessState}
                onAdd={() => onBulkAdd(company.id)}
                onRemove={() => onBulkRemove(company.id)}
                addTitle='Add access for all selected users'
                removeTitle='Remove access for all selected users'
              />
            );
          }}
        />
      </RightPanel>
    </Grid>
  );
};
